import React, { useEffect, useState } from "react";
import { Play, Calendar } from "lucide-react";
import { videosApi, getYouTubeId } from "../api";
import { Reveal, PageHero } from "../components/Shared";
import Seo from "../components/Seo";

const HERO_IMAGE = "/img/hero-videos.webp";

function VideoCard({ video, index }) {
  const [playing, setPlaying] = useState(false);
  const ytId = getYouTubeId(video.url);
  const thumb = ytId ? `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg` : null;
  const dateStr = video.date
    ? new Date(`${video.date}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <Reveal delay={index % 2}>
      <article className="gee-card h-full flex flex-col" data-testid={`video-card-${video.id}`}>
        <div className="relative aspect-video bg-[#0a1826] overflow-hidden">
          {playing && ytId ? (
            <iframe
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
              title={video.title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid={`video-iframe-${video.id}`}
            />
          ) : (
            <button
              onClick={() => setPlaying(true)}
              className="group absolute inset-0 w-full h-full cursor-pointer"
              aria-label={`Play ${video.title}`}
              data-testid={`video-play-${video.id}`}
            >
              {thumb && (
                <img
                  src={thumb}
                  alt={video.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              )}
              <span className="absolute inset-0 bg-[#06121d]/30 group-hover:bg-[#06121d]/15 transition-colors duration-300" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[#29abe2] text-white shadow-[0_10px_30px_-8px_rgba(41,171,226,0.8)] transition-transform duration-300 group-hover:scale-110">
                  <Play size={24} fill="currentColor" className="ml-1" />
                </span>
              </span>
            </button>
          )}
        </div>
        <div className="p-7 flex flex-col flex-1">
          {dateStr && (
            <span className="flex items-center gap-1.5 text-xs text-[#8aa0b0] mb-2">
              <Calendar size={13} /> {dateStr}
            </span>
          )}
          <h3 className="font-serif-d text-xl text-[#0f2438] leading-snug">{video.title}</h3>
          {video.description && (
            <p className="text-[#5b6d7d] text-sm leading-relaxed mt-2">{video.description}</p>
          )}
        </div>
      </article>
    </Reveal>
  );
}

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    videosApi
      .list()
      .then(setVideos)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <div data-testid="videos-page">
      <Seo
        title="Videos"
        description="Watch race replays, winning moments and behind-the-scenes footage from the Global Equine Group team."
        image={HERO_IMAGE}
        jsonLd={videos
          .filter((v) => getYouTubeId(v.url))
          .slice(0, 20)
          .map((v) => ({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: v.title,
            description: v.description || v.title,
            thumbnailUrl: `https://i.ytimg.com/vi/${getYouTubeId(v.url)}/hqdefault.jpg`,
            embedUrl: `https://www.youtube.com/embed/${getYouTubeId(v.url)}`,
            uploadDate: v.date || undefined,
          }))}
      />
      <PageHero image={HERO_IMAGE} kicker="Watch The Winners" title="Global Equine Group Videos">
        <p className="text-[#b9c9d6] mt-6 max-w-2xl text-lg leading-relaxed">
          Race replays, winning moments and behind-the-scenes footage from the Global Equine Group
          team.
        </p>
      </PageHero>

      <section className="py-20 bg-white min-h-[50vh]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          {videos.length > 0 && (
            <div className="grid md:grid-cols-2 gap-8" data-testid="videos-grid">
              {videos.map((v, i) => (
                <VideoCard key={v.id} video={v} index={i} />
              ))}
            </div>
          )}
          {loaded && videos.length === 0 && (
            <div className="text-center py-24" data-testid="videos-empty-state">
              <p className="font-serif-d text-2xl text-[#0f2438] mb-2">No videos yet</p>
              <p className="text-[#8aa0b0]">Check back soon for race replays and highlights.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
